import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FaTasks, FaPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { FiEdit2, FiSettings } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Task, TaskStatus } from '../../../../types/task';
import { taskService } from '../../../../services/taskService';
import Avatar from '../../../../components/misc/Avatar';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import useTasks from '../../../../hooks/community/useTasks';
import useTaskStatuses from '../../../../hooks/community/useTaskStatuses';
import EditTaskModal from '../../../../components/modals/EditTaskModal';
import CommunityHeader from '../../../../components/headers/CommunityHeader';
import CreateTaskModal, { ManageStatusesModal } from '../../../../components/modals/CreateTaskModal';
import HorizontalSponsorsList from '../../../../components/misc/HorizontalSponsorsList';

const isClosedStatus = (status?: TaskStatus): boolean => status?.systemKey === 'CLOSED';

const getStatusLabel = (status: TaskStatus, t: (key: string) => string): string => {
  if (status.systemKey === 'OPEN') return t('page.dashboard.tasks.defaultOpenStatus');
  if (status.systemKey === 'CLOSED') return t('page.dashboard.tasks.defaultClosedStatus');
  return status.name;
};

export default function CommunityTasksPage() {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const allTasks = useTasks();
  const { statuses, createStatus, updateStatus, deleteStatus, reload: reloadStatuses } = useTaskStatuses(community?.id);

  const canCreateTasks = React.useMemo(() => {
    if (!community) return false;
    return community.role === 'ADMIN' || !!community.memberPermissions?.TASKS?.CREATE;
  }, [community]);

  const canEditTasks = React.useMemo(() => {
    if (!community) return false;
    return community.role === 'ADMIN' || !!community.memberPermissions?.TASKS?.UPDATE;
  }, [community]);

  const canDeleteTasks = React.useMemo(() => {
    if (!community) return false;
    return community.role === 'ADMIN' || !!community.memberPermissions?.TASKS?.DELETE;
  }, [community]);

  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageStatuses, setShowManageStatuses] = useState(false);
  const [showClosedTasks, setShowClosedTasks] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [draggedTaskId, setDraggedTaskId] = useState<number | null>(null);
  const [dragOverGroupKey, setDragOverGroupKey] = useState<string | null>(null);

  const [loadingTasks, setLoadingTasks] = useState(false);
  const location = useLocation();
  const [highlightedTaskId, setHighlightedTaskId] = useState<number | null>(null);

  useEffect(() => {
    const openTaskId = (location.state as any)?.openTaskId;
    if (openTaskId) {
      setHighlightedTaskId(Number(openTaskId));
      const el = document.getElementById(`task-row-${openTaskId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setHighlightedTaskId(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.state]);

  const loadTasks = () => {
    if (!community?.id) return Promise.resolve();
    setLoadingTasks(true);
    return taskService
      .getAllTasksForCommunity(community.id)
      .catch((error) => {
        console.error('Failed to load tasks:', error);
        if (!navigator.onLine) {
          toast.error(t('error.offline'));
        } else {
          toast.error(t('error.loadFailed'));
        }
      })
      .finally(() => setLoadingTasks(false));
  };

  useEffect(() => {
    loadTasks();
  }, [community?.id]);

  const handleTaskStatusChange = (task: Task, newStatusId: number | null) => {
    if (!community?.id || !task.taskId) return;
    taskService.updateTask(community.id, task.taskId, { statusId: newStatusId }).catch(() => {
      toast.error(t('error.updateFailed'));
    });
  };

  const handleDrop = (groupKey: string) => {
    if (!community?.id || !draggedTaskId) return;
    const newStatusId = groupKey === 'unassigned' ? null : Number(groupKey);
    const draggedTask = allTasks.find((task) => task.taskId === draggedTaskId);
    if (!draggedTask) return;
    const currentStatusId = draggedTask.status?.statusId ?? null;
    if (currentStatusId === newStatusId) return;
    taskService.updateTask(community.id, draggedTaskId, { statusId: newStatusId }).catch(() => {
      toast.error(t('error.updateFailed'));
    });
    setDraggedTaskId(null);
    setDragOverGroupKey(null);
  };

  // Group tasks by status (in status order), unassigned tasks last
  const taskGroups = React.useMemo(() => {
    const visibleTasks = showClosedTasks ? allTasks : allTasks.filter((task) => !isClosedStatus(task.status));
    const visibleStatuses = showClosedTasks ? statuses : statuses.filter((status) => !isClosedStatus(status));
    const sorted = [...visibleStatuses].sort((a, b) => a.order - b.order);
    const groups: { key: string; label: string; color: string; tasks: Task[] }[] = sorted.map((s) => ({
      key: String(s.statusId),
      label: getStatusLabel(s, t),
      color: s.color,
      tasks: visibleTasks.filter((task) => task.status?.statusId === s.statusId),
    }));
    const unassigned = visibleTasks.filter((task) => !task.status);
    if (unassigned.length > 0) {
      groups.push({
        key: 'unassigned',
        label: t('page.dashboard.tasks.noStatus'),
        color: '#6B7280',
        tasks: unassigned,
      });
    }
    return groups;
  }, [allTasks, statuses, showClosedTasks, t]);

  const isSectionOpen = (key: string) => openSections[key] !== false;

  const toggleSection = (key: string) => setOpenSections((prev) => ({ ...prev, [key]: !isSectionOpen(key) }));

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isOverdue = (dueDate: Date | string | undefined) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (!community) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-secondary-600">{t('page.community.selectCommunity')}</p>
      </div>
    );
  }

  const isEmpty = allTasks.length === 0 && !loadingTasks;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <CommunityHeader
        title={t('page.dashboard.tasks.title')}
        action={
          !isEmpty ? (
            <div className="flex w-full gap-2 sm:w-auto">
              <button
                type="button"
                onClick={() => setShowClosedTasks((prev) => !prev)}
                className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-opacity-90">
                <span className="truncate">
                  {showClosedTasks ? t('page.dashboard.tasks.hideClosed') : t('page.dashboard.tasks.showClosed')}
                </span>
              </button>
              {canCreateTasks && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowManageStatuses(true)}
                    className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-opacity-90">
                    <FiSettings />
                    <span className="truncate">{t('page.dashboard.tasks.manageStatuses')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-opacity-90">
                    <FaPlus />
                    <span className="truncate">{t('page.dashboard.tasks.create')}</span>
                  </button>
                </>
              )}
            </div>
          ) : undefined
        }
      />

      <HorizontalSponsorsList placement="TASKS" />

      <CreateTaskModal
        open={showCreateModal}
        setOpen={setShowCreateModal}
        onSuccess={() => {
          setShowCreateModal(false);
          loadTasks();
          reloadStatuses();
        }}
      />

      <EditTaskModal
        open={showEditTaskModal}
        setOpen={(v) => {
          setShowEditTaskModal(v);
          if (!v) setEditingTask(null);
        }}
        task={editingTask}
        onSave={() => {
          setShowEditTaskModal(false);
          setEditingTask(null);
          loadTasks();
        }}
        onDelete={
          canDeleteTasks && editingTask?.taskId
            ? async () => {
                await taskService.deleteTask(community.id, editingTask.taskId!);
                setShowEditTaskModal(false);
                setEditingTask(null);
              }
            : undefined
        }
      />

      <ManageStatusesModal
        open={showManageStatuses}
        setOpen={setShowManageStatuses}
        statuses={statuses}
        onCreate={createStatus}
        onUpdate={(id, data) => updateStatus(id, data)}
        onDelete={deleteStatus}
        onReload={reloadStatuses}
      />

      {/* Content */}
      {loadingTasks ? (
        <div className="flex justify-center py-12">
          <div className="text-secondary-600 text-lg">{t('page.dashboard.tasks.loading')}</div>
        </div>
      ) : allTasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <FaTasks className="mb-4 h-16 w-16 text-primary" />
          <h3 className="text-xl font-semibold text-primary">{t('page.community.empty.tasks')}</h3>
          {canCreateTasks && (
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="mt-4 flex items-center gap-2 rounded-lg bg-primary px-6 py-2 text-white transition-colors hover:bg-opacity-90">
              <FaPlus />
              {t('page.dashboard.tasks.createFirst')}
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {taskGroups.map((group) => (
            <div
              key={group.key}
              className={`rounded-xl transition-colors ${dragOverGroupKey === group.key ? 'ring-2 ring-primary' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverGroupKey(group.key);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverGroupKey(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(group.key);
              }}>
              <button
                type="button"
                onClick={() => toggleSection(group.key)}
                className={`mb-2 flex w-full items-center justify-between rounded-t-lg border border-secondary-50 px-4 py-2 text-left transition-colors md:mb-0 ${dragOverGroupKey === group.key ? 'bg-primary/10' : 'bg-secondary-200'}`}>
                <h2 className="flex items-center gap-2 font-semibold">
                  <span
                    className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: group.color }}
                  />
                  {group.label} ({group.tasks.length})
                </h2>
                {isSectionOpen(group.key) ? (
                  <FaChevronUp className="text-primary" />
                ) : (
                  <FaChevronDown className="text-primary" />
                )}
              </button>

              {isSectionOpen(group.key) && (
                <>
                  {/* Mobile list view */}
                  <div className="flex flex-col gap-2 md:hidden">
                    {group.tasks.length === 0 ? (
                      <div className="px-4 py-2 text-center text-sm text-details/60">
                        {t('page.dashboard.tasks.noTasksInStatus')}
                      </div>
                    ) : (
                      group.tasks.map((task) => (
                        <div
                          id={`task-row-${task.taskId}`}
                          key={task.taskId}
                          draggable
                          onDragStart={() => setDraggedTaskId(task.taskId ?? null)}
                          onDragEnd={() => {
                            setDraggedTaskId(null);
                            setDragOverGroupKey(null);
                          }}
                          onClick={() => {
                            if (canEditTasks && task.taskId) {
                              setEditingTask(task);
                              setShowEditTaskModal(true);
                            }
                          }}
                          className={`flex items-center justify-between gap-3 rounded-lg border border-secondary-50 bg-secondary-50 px-4 py-2 transition-opacity ${highlightedTaskId === task.taskId ? 'bg-primary/10' : ''} ${canEditTasks ? 'cursor-pointer' : ''} ${draggedTaskId === task.taskId ? 'opacity-40' : ''}`}>
                          <div className="flex min-w-0 items-center gap-3">
                            <RowStatusPicker task={task} statuses={statuses} onStatusChange={handleTaskStatusChange} />
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-primary">{task.name}</div>
                              {task.dueDate && (
                                <div className="text-secondary-600 text-xs">
                                  {formatDate(task.dueDate)}
                                  {isOverdue(task.dueDate) && ` (${t('page.dashboard.tasks.overdue')})`}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {task.assignees && task.assignees.length > 0 ? (
                              <div className="flex items-center -space-x-2">
                                {task.assignees.slice(0, 3).map((assignee) => (
                                  <Avatar
                                    key={assignee.userId}
                                    src={undefined}
                                    alias={`${assignee.firstName} ${assignee.lastName}`}
                                    className="h-6 w-6 border-2 border-white"
                                  />
                                ))}
                                {task.assignees.length > 3 && (
                                  <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-secondary-200 text-xs">
                                    +{task.assignees.length - 3}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-secondary-400">-</span>
                            )}
                            {canEditTasks && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTask(task);
                                  setShowEditTaskModal(true);
                                }}
                                aria-label={t('general.edit')}
                                className="rounded bg-secondary-50 p-2 text-primary">
                                <FiEdit2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Table for md+ */}
                  <div className="hidden md:block">
                    <table className="w-full rounded-b-xl border border-secondary-50">
                      <thead>
                        <tr className="text-left text-sm text-details/60">
                          <th className="w-8 px-6 py-3 font-medium">
                            <span className="sr-only">{t('page.dashboard.tasks.status')}</span>
                          </th>
                          <th className="px-6 py-3 font-medium">{t('page.dashboard.tasks.description')}</th>
                          <th className="px-6 py-3 font-medium">{t('page.dashboard.tasks.dueDate')}</th>
                          <th className="px-6 py-3 font-medium">{t('page.dashboard.tasks.assignedTo')}</th>
                          {canEditTasks && (
                            <th className="px-6 py-3 text-right font-medium">{t('general.actions') || 'Actions'}</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {group.tasks.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-2 text-center">
                              {t('page.dashboard.tasks.noTasksInStatus')}
                            </td>
                          </tr>
                        ) : (
                          group.tasks.map((task) => (
                            <tr
                              id={`task-row-${task.taskId}`}
                              key={task.taskId}
                              draggable
                              onDragStart={() => setDraggedTaskId(task.taskId ?? null)}
                              onDragEnd={() => {
                                setDraggedTaskId(null);
                                setDragOverGroupKey(null);
                              }}
                              onClick={() => {
                                if (canEditTasks && task.taskId) {
                                  setEditingTask(task);
                                  setShowEditTaskModal(true);
                                }
                              }}
                              className={`border-t border-secondary-50 transition-opacity ${highlightedTaskId === task.taskId ? 'bg-primary/10' : ''} ${canEditTasks ? 'cursor-pointer hover:bg-secondary-50' : ''} ${draggedTaskId === task.taskId ? 'opacity-40' : ''}`}>
                              <td className="px-4 py-2">
                                <RowStatusPicker
                                  task={task}
                                  statuses={statuses}
                                  onStatusChange={handleTaskStatusChange}
                                />
                              </td>
                              <td className="px-4 py-2">
                                <span className="font-medium text-primary">{task.name}</span>
                              </td>
                              <td className="px-4 py-2">
                                {task.dueDate ? (
                                  <span
                                    className={`${isOverdue(task.dueDate) ? 'font-medium text-red-600' : 'text-secondary-600'}`}>
                                    {formatDate(task.dueDate)}
                                    {isOverdue(task.dueDate) && ` (${t('page.dashboard.tasks.overdue')})`}
                                  </span>
                                ) : (
                                  <span className="text-secondary-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                {task.assignees && task.assignees.length > 0 ? (
                                  <div className="flex items-center -space-x-2">
                                    {task.assignees.slice(0, 3).map((assignee) => (
                                      <Avatar
                                        key={assignee.userId}
                                        src={undefined}
                                        alias={`${assignee.firstName} ${assignee.lastName}`}
                                        className="h-8 w-8 border-2 border-white"
                                      />
                                    ))}
                                    {task.assignees.length > 3 && (
                                      <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-secondary-200 text-xs">
                                        +{task.assignees.length - 3}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span>-</span>
                                )}
                              </td>
                              {canEditTasks && (
                                <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingTask(task);
                                      setShowEditTaskModal(true);
                                    }}
                                    aria-label={t('general.edit')}
                                    className="rounded p-2 text-primary hover:bg-secondary-50">
                                    <FiEdit2 className="h-4 w-4" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RowStatusPicker({
  task,
  statuses,
  onStatusChange,
}: {
  task: Task;
  statuses: TaskStatus[];
  onStatusChange: (task: Task, statusId: number | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label={t('page.dashboard.tasks.changeStatus')}
        className="inline-block h-3.5 w-3.5 flex-shrink-0 rounded-full border border-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
        style={{ backgroundColor: task.status?.color ?? '#6B7280' }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <div className="absolute left-0 top-5 z-50 min-w-[160px] overflow-hidden rounded-lg border border-secondary-200 bg-white shadow-lg">
          {statuses.map((s) => (
            <button
              key={s.statusId}
              type="button"
              onMouseDown={(e) => {
                e.stopPropagation();
                onStatusChange(task, s.statusId);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-primary hover:bg-secondary-50">
              <span
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              {getStatusLabel(s, t)}
            </button>
          ))}
          <button
            type="button"
            onMouseDown={(e) => {
              e.stopPropagation();
              onStatusChange(task, null);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-details-500 hover:bg-secondary-50">
            <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full border border-details-500" />
            {t('page.dashboard.tasks.noStatus')}
          </button>
        </div>
      )}
    </div>
  );
}
